import Avo from 'analytics/Avo';
import type { Addresses } from 'api/types/addresses.types';
import { DeleteIcon, EditIcon, LocationIcon } from 'assets/icons';
import cx from 'classnames';
import { useConfirmationDialog } from 'components/molecules/ConfirmationModal';
import RadioButton from 'components/molecules/RadioButton';
import i18n from 'i18n/config';
import { AddressSheetContext, type AddressSheetContextProviderProps } from 'packages/shared/newOrder/provider/AddressSheetContextProvider';
import { selectAddress } from 'pages/newOrder/newOrder.slice';
import { useDeleteCustomerAddress } from 'pages/queries/address.queries';
import React, { useContext } from 'react';
import Button from 'react-bootstrap/Button';
import Col from 'react-bootstrap/Col';
import ListGroupItem from 'react-bootstrap/ListGroupItem';
import Row from 'react-bootstrap/Row';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import styled, { useTheme } from 'styled-components';
import { useBasePath } from 'utils/router/hooks/useBasePath';

import { addressNameRenderer } from './constants';

interface Props {
  address: Addresses.Address;
  disableEdit?: boolean;
}

const StyledListGroupItem = styled(ListGroupItem)`
  margin: 8px;
  background-color: #fff;
  border: 1px solid #eaedf1;
  border-radius: 8px;
  &.selected {
    border-color: ${({ theme }) => theme.components.addressSheet.addressBox.selected.borderColor};
    background-color: ${({ theme }) => theme.components.addressSheet.addressBox.selected.backgroundColor};
  }
`;

const StyledCol = styled(Col)`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
`;

const AddressBox: React.FC<Props> = ({ address, disableEdit = false }) => {
  const dispatch = useDispatch();
  const { getConfirmation } = useConfirmationDialog();
  const { mutateAsync: deleteCustomerAddress } = useDeleteCustomerAddress();
  const navigate = useNavigate();
  const basePath = useBasePath();
  const { t } = useTranslation();
  const { onEdit } = useContext(AddressSheetContext) as AddressSheetContextProviderProps;
  const theme = useTheme();
  const handleDeleteAddress = async (event: React.MouseEvent<HTMLElement, MouseEvent>) => {
    event.stopPropagation();
    const confirmed = await getConfirmation({
      title: t('common.deleteAddressQuestion'),
    });
    if (confirmed) {
      deleteCustomerAddress({ id: address.id });
    }
  };

  const handleAddressSelected = () => {
    dispatch(selectAddress(address.id));
    Avo.addressSelected({
      addressGeofence: address.geofenceLabel,
      cityName: address.cityId ?? '',
      screen: basePath ?? 'unknown',
    });
  };

  const handleEditAddress = (event: React.MouseEvent<HTMLElement, MouseEvent>) => {
    if (onEdit) {
      onEdit(address);
    } else {
      event.stopPropagation();
      navigate(`${basePath}/editAddress`, {
        state: {
          address,
        },
      });
    }
  };

  const isRTL = i18n.dir() === 'rtl';
  return (
    <StyledListGroupItem className={cx('p-2', { selected: address.isInUse })} onClick={handleAddressSelected} as="div">
      <Row noGutters className="align-items-center">
        <Col xs="auto">
          <LocationIcon />
        </Col>
        <Col xs>
          <Row noGutters className="align-items-center margin-start-3">
            <Col xs>
              <h4 className="mb-0 font-weight-normal">{t(`common.addressTypes.${address.addressType}`)}</h4>
              <h4 className="text-gray-dark font-weight-light mb-0">{addressNameRenderer(address)}</h4>
            </Col>
            <StyledCol xs="auto">
              <Row noGutters>
                {!address.isInUse && !disableEdit && (
                  <Col xs="auto">
                    <Button variant="link" onClick={handleDeleteAddress}>
                      <DeleteIcon />
                    </Button>
                  </Col>
                )}
                {!disableEdit && (
                  <Col xs="auto">
                    <Button variant="link" onClick={handleEditAddress}>
                      <EditIcon className={isRTL ? 'transform-270' : ''} />
                    </Button>
                  </Col>
                )}
              </Row>
              {theme.components.addressSheet.addressBox.isShowRadioButton && <RadioButton isActive={address.isInUse} className="mx-4 mb-2 mt-4" />}
            </StyledCol>
          </Row>
        </Col>
      </Row>
    </StyledListGroupItem>
  );
};

export default AddressBox;
